import type { FC } from "react";

import { Box, Text, Tooltip } from "@hightouch/htui";

import { colors } from "src/components/explore/visual/colors";
import { ConditionType } from "src/types/visual";

type Props = {
  conditionType: ConditionType.And | ConditionType.Or;
  /** Omitted for the first condition in a group, which renders as a bracket
   * rather than an operator. */
  onToggle?: () => void;
  isDisabled?: boolean;
};

/**
 * The compact builder's left rail. It replaces both the wider
 * `GroupIndicatorBar` and the full-width AND/OR row below each condition:
 * conditions after the first carry the operator inline as a full-height chip,
 * which is what removes 48px per condition.
 *
 * The operator belongs to the group, not the row, so every chip in a group
 * shows the same word and toggling any one of them toggles all of them.
 */
export const CompactOperatorRail: FC<Readonly<Props>> = ({
  conditionType,
  onToggle,
  isDisabled = false,
}) => {
  if (!onToggle) {
    // The leading condition has no preceding condition to join, so its rail is
    // a plain tinted bracket rather than an operator.
    return (
      <Box
        flexShrink={0}
        width={2}
        bg={colors.base[conditionType]}
        borderTopLeftRadius="lg"
        borderBottomLeftRadius="lg"
      />
    );
  }

  const label = conditionType === ConditionType.And ? "AND" : "OR";
  const toggleTo = conditionType === ConditionType.And ? "OR" : "AND";

  return (
    <Tooltip message={`Toggle to ${toggleTo}`}>
      <Box
        as="button"
        type="button"
        aria-label={`Toggle to ${toggleTo}`}
        disabled={isDisabled}
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
        // Fixed rather than content-sized: "AND" is wider than "OR", so a
        // content-sized chip would resize when toggled — shifting every card in
        // the group sideways, including the chip under the cursor.
        width={9}
        bg={colors.base[conditionType]}
        color={colors.text[conditionType]}
        borderTopLeftRadius="lg"
        borderBottomLeftRadius="lg"
        cursor={isDisabled ? "not-allowed" : "pointer"}
        transitionProperty="background-color, color"
        transitionDuration="fast"
        transitionTimingFunction="easeOutQuad"
        _hover={
          isDisabled
            ? undefined
            : {
                bg: colors.hover[conditionType],
                color: colors.textHover[conditionType],
              }
        }
        _focusVisible={{ boxShadow: "outline", outline: "none" }}
        onClick={onToggle}
      >
        {/* htui `Text` renders a <p>; the chip is a button, so the label has to
            be inline to keep the button's content model valid. */}
        <Text as="span" size="sm" fontWeight="semibold" color="inherit">
          {label}
        </Text>
      </Box>
    </Tooltip>
  );
};
